import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty, Stat } from "@/components/app/Primitives";
import { Building2, Users, Clock, AlertTriangle, Palmtree, X } from "lucide-react";

export default function OwnerDashboard() {
    const [data, setData] = useState(null);
    const [focus, setFocus] = useState(null); // company name filter for the drill-down lists
    const load = () => api.get("/owner/overview").then((r) => setData(r.data)).catch(() => {});
    useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
    if (!data) return <div className="text-sm text-muted-foreground">Loading overview…</div>;
    const t = data.totals;

    const late = focus ? data.late_today.filter((p) => p.company === focus) : data.late_today;
    const leave = focus ? data.on_leave_today.filter((p) => p.company === focus) : data.on_leave_today;

    return (
        <div>
            <PageHeader title="Owner Overview" subtitle={`All companies at a glance — ${data.date}. Click a company row to drill into who is late or on leave there.`} />

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Stat label="Companies" value={data.companies.length} icon={Building2} tone="primary" />
                <Stat label="Total headcount" value={t.headcount} icon={Users} />
                <Stat label="Clocked in" value={t.present} icon={Clock} />
                <Stat label="Late today" value={t.late} icon={AlertTriangle} />
                <Stat label="On leave" value={t.on_leave} icon={Palmtree} />
            </div>

            <Surface className="p-4 mt-4">
                <h3 className="font-display font-semibold mb-2">Per company</h3>
                <TableWrap>
                    <thead><tr><th>Company</th><th>Region</th><th className="num">Headcount</th><th className="num">Clocked in</th><th className="num">Late</th><th className="num">On leave</th><th className="num">Not in</th></tr></thead>
                    <tbody>
                        {data.companies.map((c) => (
                            <tr key={c.company_id}
                                className={`cursor-pointer ${focus === c.name ? "bg-primary/5" : "hover:bg-secondary/50"}`}
                                onClick={() => setFocus(focus === c.name ? null : c.name)}
                                data-testid={`owner-co-${c.code || c.company_id}`}>
                                <td><b>{c.name}</b> <span className="text-xs text-muted-foreground">{c.code}</span>{focus === c.name && <span className="ml-2 text-[10px] text-primary font-semibold">● drilled in</span>}</td>
                                <td className="font-mono text-xs">{c.region || "—"}</td>
                                <td className="num font-semibold">{c.headcount}</td>
                                <td className="num text-emerald-600 font-semibold">{c.present}</td>
                                <td className="num">{c.late ? <span className="badge badge-pending">{c.late}</span> : "0"}</td>
                                <td className="num">{c.on_leave ? <span className="badge" style={{ background: "#FFF4E5", color: "#B45309" }}>{c.on_leave}</span> : "0"}</td>
                                <td className="num text-muted-foreground">{c.not_in}</td>
                            </tr>
                        ))}
                    </tbody>
                </TableWrap>
            </Surface>

            <div className="grid gap-4 lg:grid-cols-2 mt-4">
                <Surface className="p-4">
                    <h3 className="font-display font-semibold mb-2 flex items-center gap-2">
                        <AlertTriangle className="size-4 text-amber-500" /> Late today ({late.length})
                        {focus && <button className="ml-auto text-xs text-primary flex items-center gap-1" onClick={() => setFocus(null)} data-testid="clear-focus"><X className="size-3" /> {focus} only — clear</button>}
                    </h3>
                    {!late.length && <Empty title={focus ? `Nobody late at ${focus}` : "Nobody late today"} />}
                    <div className="space-y-1.5">
                        {late.map((p, i) => (
                            <div key={i} className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5">
                                <div className="text-sm"><b>{p.name}</b> <span className="text-xs text-muted-foreground">· {p.company}{p.position ? ` · ${p.position}` : ""}</span></div>
                                <span className="badge badge-pending">late {p.late_minutes}m</span>
                            </div>
                        ))}
                    </div>
                </Surface>
                <Surface className="p-4">
                    <h3 className="font-display font-semibold mb-2 flex items-center gap-2">
                        <Palmtree className="size-4 text-orange-500" /> On leave today ({leave.length})
                        {focus && <span className="ml-auto text-xs text-muted-foreground">{focus} only</span>}
                    </h3>
                    {!leave.length && <Empty title={focus ? `Nobody on leave at ${focus}` : "Nobody on leave today"} />}
                    <div className="space-y-1.5">
                        {leave.map((p, i) => (
                            <div key={i} className="flex items-center justify-between rounded-md border border-orange-200 bg-orange-50 px-3 py-1.5">
                                <div className="text-sm"><b>{p.name}</b> <span className="text-xs text-muted-foreground">· {p.company}{p.position ? ` · ${p.position}` : ""}</span></div>
                                <span className="badge" style={{ background: "#FFF4E5", color: "#B45309" }}>{p.leave_type}</span>
                            </div>
                        ))}
                    </div>
                </Surface>
            </div>
        </div>
    );
}
